import React, { useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { apiFetch, zeigeApiFehler } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { oeffneMailVerfassen, oeffneMailVonAussen, oeffneTerminBearbeiten, oeffneKlassenAktionen, oeffneNotizen, oeffneBlatt } from '../../../lib/nav';
import type { Schueler } from '../../../types';
import {
  Ueberblick, StehtAnEintrag, ElternKontakt, FachNote, VerlaufTyp, VerlaufEintrag, MitarbeitWoche, useVerlauf,
} from './profil-api';
import { note, notenWert, tm, wt, wtTm, wtKommaTm, heuteIso } from './format';

// Welle P1d: 'eltern' und 'notizen' gibt es nur am Handy als eigene Bereich-Seite
export type ProfilTab = 'ueberblick' | 'noten' | 'fehlzeiten' | 'mitarbeit' | 'gespraeche' | 'verlauf' | 'eltern' | 'notizen';

interface Props {
  s: Schueler;
  daten: Ueberblick | null;
  fehler: boolean;
  neuLaden: () => void;
  isMobile: boolean;
  onTab: (tab: ProfilTab, anker?: string) => void;
  onNeuerPunkt: () => void;
  onMitarbeitWoche: (fach: string, wocheVon?: string) => void;
  onTermin: () => void;
  /** Welle B2: Gespräch aus einem bestätigten Eltern-Termin / „Nicht nötig“ */
  onGespraechAusTermin?: (terminId: number) => void;
  onGespraechNichtNoetig?: (terminId: number) => void;
  onElternNeu: () => void;
  onElternBearbeiten: (e: ElternKontakt) => void;
  notizen: React.ReactNode;
}

// Welle P1b: Überblick — vier Kennzahlen, „Steht an“, Verlauf, Eltern, Notizen.
// Alle Noten kommen vom Server (P1a, berechneZeugnisnote); jede Karte unterscheidet
// „leer“ und „konnte nicht geladen werden“.
export function ProfilUeberblick(p: Props) {
  return (
    <div className="ld-pu">
      <div className="ld-pu__kennzahlen">
        <FehlzeitenKarte {...p} />
        <NotenKarte {...p} />
        <MitarbeitKarte {...p} />
        <TerminKarte {...p} />
      </div>
      <div className="ld-pu__spalten">
        <div className="ld-pu__links">
          <StehtAnKarte {...p} />
          <VerlaufKarte {...p} />
        </div>
        <div className="ld-pu__rechts">
          <ElternKarte {...p} />
          {p.notizen}
        </div>
      </div>
    </div>
  );
}

// ── Welle P1d: Profil-Start am Handy — „Steht an“ (höchstens 3) und eine Zeile je Bereich ──
export function ProfilStartMobil(p: Props & { hinweise?: React.ReactNode }) {
  return (
    <div className="ld-pu ld-ps">
      <StehtAnKarte {...p} max={3} />
      {p.hinweise}
      <BereicheListe {...p} />
    </div>
  );
}

/** Welle P1d: Eltern als eigene Bereich-Seite am Handy (Anlegen über die Leiste unten) */
export function ElternBereich(p: Props) {
  return <ElternKarte {...p} />;
}

type BereichZeile = { tab: ProfilTab; icon: string; titel: string; text: React.ReactNode };
function BereicheListe({ s, daten, fehler, neuLaden, onTab }: Props) {
  return (
    <nav className="ld-ps-liste" aria-label="Bereiche">
      <Zustand daten={daten} fehler={fehler} neuLaden={neuLaden}>{() => {
        const d = daten!;
        const zeilen: BereichZeile[] = [
          { tab: 'noten', icon: 'bar_chart', titel: 'Noten', text: notenKurz(d) },
          { tab: 'fehlzeiten', icon: 'event_busy', titel: 'Fehlzeiten', text: fehlzeitenKurz(d) },
          { tab: 'mitarbeit', icon: 'back_hand', titel: 'Mitarbeit', text: mitarbeitKurz(d) },
          { tab: 'gespraeche', icon: 'forum', titel: 'Gespräche', text: gespraecheKurz(d) },
          { tab: 'eltern', icon: 'family_restroom', titel: 'Eltern', text: elternKurz(d) },
          { tab: 'notizen', icon: 'sticky_note_2', titel: 'Notizen', text: notizenKurz(d) },
          { tab: 'verlauf', icon: 'history', titel: 'Verlauf', text: 'Alles der Reihe nach' },
        ];
        return (<>
          {zeilen.map(z => (
            <button key={z.tab} type="button" className="ld-ps-zeile" onClick={() => onTab(z.tab)}>
              <span className="ld-ps-zeile__icon" aria-hidden="true"><MIcon name={z.icon} size={20} /></span>
              <span className="ld-ps-zeile__text"><b>{z.titel}</b><span>{z.text}</span></span>
              <MIcon name="chevron_right" size={20} className="ld-ps-zeile__pfeil" />
            </button>
          ))}
          {/* Welle B4: Blatt fürs Gespräch (am PC als Symbol im Kopf) */}
          <button type="button" className="ld-ps-zeile" onClick={() => oeffneBlatt({ schuelerId: s.id, name: d.kopf.name ?? s.name })}>
            <span className="ld-ps-zeile__icon" aria-hidden="true"><MIcon name="description" size={20} /></span>
            <span className="ld-ps-zeile__text"><b>Blatt fürs Gespräch</b><span>Eine Seite als PDF, Abschnitte wählbar</span></span>
            <MIcon name="chevron_right" size={20} className="ld-ps-zeile__pfeil" />
          </button>
        </>);
      }}</Zustand>
    </nav>
  );
}
const mitGrundlage = (n: FachNote) => n.status === 'final' || n.grundlage.ka_geschrieben > 0 || n.grundlage.wochen_beobachtet + (n.grundlage.einzelnoten_gewicht ?? 0) > 0;
function notenKurz(d: Ueberblick): React.ReactNode {
  if (d.noten.length === 0) return 'Noch kein Fach eingetragen';
  return d.noten.map(n => `${n.fach} ${n.status === 'offen' || !mitGrundlage(n) ? 'noch offen' : notenWert(n)}`).join(', ');
}
function fehlzeitenKurz(d: Ueberblick): React.ReactNode {
  const { unentschuldigt: u, entschuldigt: e, schwellen } = d.fehlzeiten;
  if (u === 0 && e === 0) return 'Keine Fehlzeiten in diesem Schuljahr';
  // Rot nur ab §68 (Schwelle der Lehrkraft), sonst amber
  const ton = u >= schwellen.rot ? 'ld-ps-ton--rot' : u > 0 ? 'ld-ps-ton--amber' : '';
  return <span className={ton}>{u} Std. unentschuldigt, {e} entschuldigt</span>;
}
function mitarbeitKurz(d: Ueberblick): React.ReactNode {
  // Ø nur über Fächer mit mindestens einer Beobachtung (sonst wäre es die Grundannahme)
  const beobachtet = d.mitarbeit.filter(m => (d.noten.find(n => n.fach === m.fach)?.grundlage.wochen_beobachtet ?? 0) > 0 && m.schnitt != null);
  // Lücken: das Fach mit den meisten Wochen ohne Beobachtung (Wochen nicht über Fächer doppelt zählen)
  const luecken = Math.max(0, ...d.mitarbeit.map(m => m.anzahl_luecken));
  const hinweis = luecken > 0 ? <em className="ld-ps-ton--amber">{luecken === 1 ? '1 Woche' : `${luecken} Wochen`} ohne Beobachtung</em> : null;
  if (beobachtet.length === 0) return hinweis ? <>Noch keine Beobachtung, {hinweis}</> : 'Noch keine Beobachtung';
  const schnitt = beobachtet.reduce((a, m) => a + (m.schnitt as number), 0) / beobachtet.length;
  return <>Ø {note(Math.round(schnitt * 10) / 10)}{hinweis && <>, {hinweis}</>}</>;
}
function gespraecheKurz(d: Ueberblick): React.ReactNode {
  const g = d.bereiche?.gespraeche;
  // Welle Z2: der nächste bestätigte Eltern-Termin steht zuerst
  const termin = g?.naechster_termin ? `Termin am ${tm(g.naechster_termin.datum)}` : null;
  if (!g || g.anzahl === 0) return termin ?? 'Noch kein Gespräch dokumentiert';
  return [termin, `${termin ? 'zuletzt' : 'Zuletzt'} ${tm(g.zuletzt!)}`, g.nachfassen_am ? `nachfassen am ${tm(g.nachfassen_am)}` : null].filter(Boolean).join(', ');
}
function elternKurz(d: Ueberblick): React.ReactNode {
  const hk = d.eltern.find(e => e.hauptkontakt) ?? d.eltern[0];
  if (!hk) return 'Noch kein Kontakt hinterlegt';
  return `${hk.name || ROLLE[hk.rolle]}${hk.name ? ` (${ROLLE[hk.rolle]})` : ''}${hk.hauptkontakt ? ', Hauptkontakt' : ''}`;
}
function notizenKurz(d: Ueberblick): React.ReactNode {
  const n = d.bereiche?.notizen.im_schuljahr ?? 0;
  return n === 0 ? 'Noch keine in diesem Schuljahr' : `${n} in diesem Schuljahr`;
}

// ── gemeinsame Bausteine ─────────────────────────────────────────────────────
function Karte({ icon, titel, rechts, children, className = '', klein = false }: {
  icon: string; titel: React.ReactNode; rechts?: React.ReactNode; children: React.ReactNode; className?: string; klein?: boolean;
}) {
  return (
    <section className={`ld-pu-karte ${klein ? 'ld-pu-karte--kennzahl' : ''} ${className}`}>
      <header className="ld-pu-karte__kopf">
        <MIcon name={icon} size={klein ? 17 : 19} />
        <h3>{titel}</h3>
        {rechts && <div className="ld-pu-karte__rechts">{rechts}</div>}
      </header>
      {children}
    </section>
  );
}
function Fehler({ onErneut }: { onErneut: () => void }) {
  return (
    <div className="ld-pu-fehler" role="alert">
      <span>Konnte nicht geladen werden.</span>
      <button type="button" onClick={onErneut}><MIcon name="refresh" size={15} />Erneut versuchen</button>
    </div>
  );
}
const Laedt = () => <div className="ld-pu-laedt" aria-busy="true"><span /><span /></div>;
function Zustand({ daten, fehler, neuLaden, children }: { daten: unknown; fehler: boolean; neuLaden: () => void; children: () => React.ReactNode }) {
  if (fehler && !daten) return <Fehler onErneut={neuLaden} />;
  if (!daten) return <Laedt />;
  return <>{children()}</>;
}

// ── Kennzahl: Fehlzeiten ─────────────────────────────────────────────────────
function FehlzeitenKarte({ daten, fehler, neuLaden, onTab, isMobile }: Props) {
  const fz = daten?.fehlzeiten;
  return (
    <Karte klein icon="event_busy" titel={fz && !isMobile ? `Unentschuldigt · Schuljahr ${fz.schuljahr}` : 'Unentschuldigt'}>
      <Zustand daten={daten} fehler={fehler} neuLaden={neuLaden}>{() => {
        const { unentschuldigt: u, schwellen: { gelb, rot } } = fz!;
        // Rot nur für §68 (Schwelle der Lehrkraft erreicht), sonst amber bzw. ruhig
        const ton = u >= rot ? 'rot' : u >= gelb ? 'amber' : u > 0 ? 'amber-leicht' : 'ruhig';
        const skala = Math.max(rot * 1.25, u);
        const pct = (x: number) => `${Math.min(100, (x / skala) * 100)}%`;
        return (
          <button type="button" className="ld-pu-kz" onClick={() => onTab('fehlzeiten')} aria-label={`${u} Stunden unentschuldigt, Details in Fehlzeiten`}>
            <div className={`ld-pu-kz__zahl ld-pu-ton--${ton}`}>{u}<small>Std.</small></div>
            <div className="ld-pu-fzbalken" aria-hidden="true">
              <div className={`ld-pu-fzbalken__fuell ld-pu-fzbalken__fuell--${ton}`} style={{ width: pct(u) }} />
              <span className="ld-pu-fzbalken__mark" style={{ left: pct(gelb) }} />
              <span className="ld-pu-fzbalken__mark" style={{ left: pct(rot) }} />
            </div>
            <div className="ld-pu-fzskala" aria-hidden="true">
              <span>Achtung ab {gelb}</span><span>§68 ab {rot}</span>
            </div>
            <div className="ld-pu-kz__fuss">dazu <b>{fz!.entschuldigt} Std. entschuldigt</b>{fz!.davon_attest > 0 ? `, davon ${fz!.davon_attest} mit Attest` : ''}</div>
          </button>
        );
      }}</Zustand>
    </Karte>
  );
}

// ── Kennzahl: Noten ──────────────────────────────────────────────────────────
const TREND_ICON = { steigend: 'trending_up', fallend: 'trending_down', gleich: 'trending_flat' } as const;
const TREND_TEXT = { steigend: 'besser geworden', fallend: 'schlechter geworden', gleich: 'gleich geblieben' } as const;
export function grundlageText(n: FachNote): string {
  const g = n.grundlage;
  const ka = g.ka_geschrieben === 0 ? 'keine Klassenarbeit' : g.ka_geschrieben === 1 ? '1 Klassenarbeit' : `${g.ka_geschrieben} Klassenarbeiten`;
  return `${g.wochen_beobachtet} von ${g.wochen_gesamt} Wochen, ${ka}`;
}
function NotenKarte({ daten, fehler, neuLaden, onTab, isMobile }: Props) {
  const hj = daten ? (isMobile ? `Vorläufig, HJ ${daten.halbjahr.halbjahr}` : `Vorläufige Noten · HJ ${daten.halbjahr.halbjahr}`) : 'Vorläufige Noten';
  return (
    <Karte klein icon="bar_chart" titel={hj}>
      <Zustand daten={daten} fehler={fehler} neuLaden={neuLaden}>{() => {
        // ohne Klassenarbeit und ohne Beobachtung wäre der Wert nur die Grundannahme (3) — nicht zeigen
        const noten = daten!.noten.filter(n => n.status === 'final' || n.grundlage.ka_geschrieben > 0 || n.grundlage.wochen_beobachtet > 0);
        if (noten.length === 0) return <div className="ld-pu-leer">Noch keine Noten in diesem Halbjahr.</div>;
        return (
          <>
            <ul className="ld-pu-noten">
              {noten.map(n => (
                <li key={n.fach}>
                  <button type="button" onClick={() => onTab('noten')} className="ld-pu-noten__zeile">
                    <span className="ld-pu-noten__fach">{n.fach}</span>
                    {n.status === 'offen' ? (
                      <span className="ld-pu-noten__offen">
                        <b>noch offen</b>
                        <small>{n.tendenz != null ? `Tendenz ${note(n.tendenz)} · ` : ''}{grundlageText(n)}</small>
                      </span>
                    ) : (
                      <span className="ld-pu-noten__wert">
                        {notenWert(n)}
                        {n.status === 'final' ? <small className="ld-pu-noten__final">Zeugnis</small>
                          : n.trend ? <MIcon name={TREND_ICON[n.trend]} size={18} className={`ld-pu-trend ld-pu-trend--${n.trend}`} label={TREND_TEXT[n.trend]} />
                          : <span className="ld-pu-trend ld-pu-trend--leer" />}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            {noten.some(n => n.trend) && <div className="ld-pu-kz__fuss">Pfeil: Richtung seit den letzten Ferien</div>}
          </>
        );
      }}</Zustand>
    </Karte>
  );
}

// ── Kennzahl: Mitarbeit ──────────────────────────────────────────────────────
function WochenRaster({ wochen, fach, onLuecke }: { wochen: MitarbeitWoche[]; fach: string; onLuecke: (fach: string, von: string) => void }) {
  const heute = heuteIso();
  return (
    <div className="ld-pu-raster">
      {wochen.map(w => {
        const diese = w.von <= heute && heute < addTage(w.von, 7);
        const titel = `KW ${w.kw}`;
        if (w.ferien) return <span key={w.von} className="ld-pu-zelle ld-pu-zelle--ferien" title={`${titel}: ${w.ferien_name ?? 'Ferien'}`} aria-label={`${titel} Ferien`} />;
        if (w.status === 'luecke') return (
          <button key={w.von} type="button" className={`ld-pu-zelle ld-pu-zelle--luecke${diese ? ' ld-pu-zelle--jetzt' : ''}`}
            onClick={() => onLuecke(fach, w.von)} aria-label={`${titel}: noch keine Beobachtung — eintragen`} title={`${titel}: eintragen`}>+</button>
        );
        if (w.status === 'entschuldigt') return <span key={w.von} className="ld-pu-zelle ld-pu-zelle--entsch" title={`${titel}: entschuldigt gefehlt`} aria-label={`${titel} entschuldigt`}>e</span>;
        return (
          <span key={w.von} className={`ld-pu-zelle${diese ? ' ld-pu-zelle--jetzt' : ''}`} title={`${titel}: ${note(w.wert)}${w.tendenz ? ` (${w.tendenz})` : ''}`}>
            {w.wert == null ? '' : Number.isInteger(w.wert) ? w.wert : note(w.wert)}
          </span>
        );
      })}
    </div>
  );
}
function addTage(iso: string, n: number) { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
function MitarbeitKarte({ daten, fehler, neuLaden, onMitarbeitWoche }: Props) {
  return (
    <Karte klein icon="back_hand" titel="Mitarbeit">
      <Zustand daten={daten} fehler={fehler} neuLaden={neuLaden}>{() => {
        // Fächer ohne eine einzige Beobachtung im Halbjahr: kein Schnitt (wäre nur die Grundannahme 3)
        const beobachtet = (fach: string) => (daten!.noten.find(n => n.fach === fach)?.grundlage.wochen_beobachtet ?? 0) > 0;
        const liste = daten!.mitarbeit.filter(m => beobachtet(m.fach));
        // Welle P1d: Leerzustand nur Text — eingetragen wird über „Eintragen“
        if (liste.length === 0) return <div className="ld-pu-leer">Noch keine Mitarbeit eingetragen.</div>;
        const luecken = liste.reduce((a, m) => a + m.wochen.filter(w => w.status === 'luecke').length, 0);
        const hinweis = luecken > 0 && (
          <div className="ld-pu-hinweis"><MIcon name="info" size={15} />{luecken === 1 ? '1 Woche' : `${luecken} Wochen`} ohne Beobachtung</div>
        );
        if (liste.length === 1) {
          const m = liste[0];
          return (
            <>
              <div className="ld-pu-kz__zahl">{note(m.schnitt)}<small>Ø {m.fach}</small></div>
              <WochenRaster wochen={m.wochen} fach={m.fach} onLuecke={onMitarbeitWoche} />
              {hinweis}
            </>
          );
        }
        return (
          <>
            <ul className="ld-pu-mitarbeit">
              {liste.map(m => (
                <li key={m.fach}>
                  <span className="ld-pu-mitarbeit__kopf"><b>{m.fach}</b><span>Ø {note(m.schnitt)}</span></span>
                  <WochenRaster wochen={m.wochen} fach={m.fach} onLuecke={onMitarbeitWoche} />
                </li>
              ))}
            </ul>
            {hinweis}
          </>
        );
      }}</Zustand>
    </Karte>
  );
}

// ── Kennzahl: Nächster Termin ────────────────────────────────────────────────
function TerminKarte({ daten, fehler, neuLaden }: Props) {
  return (
    <Karte klein icon="event" titel="Nächster Termin">
      <Zustand daten={daten} fehler={fehler} neuLaden={neuLaden}>{() => {
        const t = daten!.naechster_termin;
        // Welle P1d: kein zweiter Weg im Leerzustand — Termin vorschlagen steht im Kopf
        if (!t) return <div className="ld-pu-leer">Kein Termin geplant.</div>;
        const inhalt = (<>
          <div className="ld-pu-kz__zahl ld-pu-kz__zahl--termin">{wtKommaTm(t.datum)}</div>
          <div className="ld-pu-kz__unter">{t.zeit ? `${t.zeit.slice(0, 5)} · ` : ''}{t.titel}</div>
        </>);
        return t.termin_id
          ? <button type="button" className="ld-pu-kz" onClick={() => oeffneTerminBearbeiten(t.termin_id!)}>{inhalt}</button>
          : <div className="ld-pu-kz">{inhalt}</div>;
      }}</Zustand>
    </Karte>
  );
}

// ── Steht an ─────────────────────────────────────────────────────────────────
function DatumBox({ e }: { e: StehtAnEintrag }) {
  if (e.art === 'paragraph68') return <span className="ld-pu-dbox ld-pu-dbox--jetzt"><small>jetzt</small><b>!</b></span>;
  if (!e.faellig_am) return <span className="ld-pu-dbox ld-pu-dbox--ohne" aria-hidden="true" />;
  return (
    <span className={`ld-pu-dbox${e.ueberfaellig ? ' ld-pu-dbox--ueber' : ''}`}>
      <small>{wt(e.faellig_am)}</small><b>{e.faellig_am.slice(8, 10)}</b>
    </span>
  );
}
function StehtAnKarte(p: Props & { max?: number }) {
  const { s, daten, fehler, neuLaden, onTab, onNeuerPunkt, max } = p;
  const [laeuft, setLaeuft] = useState<number | null>(null);
  const [alle, setAlle] = useState(false);   // Welle P1d: am Handy erst höchstens `max`, dann „Alle n“
  const vorname = daten?.kopf.vorname ?? s.name.split(' ')[0];

  async function abhaken(e: StehtAnEintrag, i: number) {
    const z = e.ziel;
    const schritte: { an: () => Promise<unknown>; aus: () => Promise<unknown> } | null =
      e.art === 'nachfassen' && z.task_id ? {
        an: () => apiFetch(`/api/tasks/${z.task_id}`, { method: 'PATCH', body: JSON.stringify({ erledigt: true }) }),
        aus: () => apiFetch(`/api/tasks/${z.task_id}`, { method: 'PATCH', body: JSON.stringify({ erledigt: false }) }),
      } : e.art === 'offener_punkt' && z.offener_punkt_id ? {
        an: () => apiFetch(`/api/schueler/offene-punkte/${z.offener_punkt_id}`, { method: 'PATCH', body: JSON.stringify({ erledigt: true }) }),
        aus: () => apiFetch(`/api/schueler/offene-punkte/${z.offener_punkt_id}`, { method: 'PATCH', body: JSON.stringify({ erledigt: false }) }),
      } : e.art === 'klassen_aktion' && z.aktion_id ? {
        an: () => apiFetch(`/api/aktionen/${z.aktion_id}/status/${s.id}`, { method: 'PATCH', body: JSON.stringify({ erledigt: true, notiz: z.notiz ?? null }) }),
        aus: () => apiFetch(`/api/aktionen/${z.aktion_id}/status/${s.id}`, { method: 'PATCH', body: JSON.stringify({ erledigt: false, notiz: z.notiz ?? null }) }),
      } : null;
    if (!schritte) return;
    setLaeuft(i);
    try {
      await schritte.an();
      neuLaden();
      toast(`„${e.titel}“ erledigt`, 'success', {
        dauerMs: 5000,
        aktion: { label: 'Rückgängig', onClick: async () => {
          try { await schritte.aus(); neuLaden(); } catch (err) { zeigeApiFehler(err, 'Rückgängig fehlgeschlagen'); }
        } },
      });
    } catch (err) { zeigeApiFehler(err, 'Abhaken fehlgeschlagen'); }
    finally { setLaeuft(null); }
  }

  function elternErinnern(e: StehtAnEintrag) {
    const eltern = daten?.eltern ?? [];
    const k = eltern.find(x => x.hauptkontakt && x.email) ?? eltern.find(x => x.email);
    if (!k?.email) { toast('Für dieses Kind ist keine Eltern-Adresse hinterlegt.', 'error'); return; }
    oeffneMailVerfassen(k.email, `Attest für ${vorname} vom ${tm(e.ziel.datum ?? e.faellig_am)}`);
  }

  function knopf(e: StehtAnEintrag, i: number): React.ReactNode {
    const k = (label: string, onClick: () => void) => (
      <button type="button" className="ld-pu-sa__knopf" onClick={onClick} disabled={laeuft === i}>{label}</button>
    );
    switch (e.art) {
      case 'paragraph68': return k('Prüfen', () => onTab('fehlzeiten', 'ld-profil-68'));
      case 'attest': return k('Eltern erinnern', () => elternErinnern(e));
      case 'entschuldigung_fehlt': return k('Ansehen', () => onTab('fehlzeiten'));
      case 'klassenarbeit': return e.ziel.termin_id ? k('Zum Termin', () => oeffneTerminBearbeiten(e.ziel.termin_id!)) : null;
      case 'elterntermin': return e.ziel.termin_id ? k('Zum Termin', () => oeffneTerminBearbeiten(e.ziel.termin_id!)) : null;   // Welle Z2
      // Welle B2: vergangener Termin ohne Gespräch
      case 'gespraech_doku': return e.ziel.termin_id ? (
        <span className="ld-pu-sa__knoepfe">
          {k('Dokumentieren', () => p.onGespraechAusTermin?.(e.ziel.termin_id!))}
          <button type="button" className="ld-pu-link" disabled={laeuft === i} onClick={() => p.onGespraechNichtNoetig?.(e.ziel.termin_id!)}>Nicht nötig</button>
        </span>
      ) : null;
      case 'nachfassen': return e.ziel.task_id ? k('Abhaken', () => abhaken(e, i)) : k('Öffnen', () => onTab('gespraeche'));
      case 'offener_punkt': return k('Abhaken', () => abhaken(e, i));
      case 'klassen_aktion': return (
        <span className="ld-pu-sa__knoepfe">
          {k('Abhaken', () => abhaken(e, i))}
          <button type="button" className="ld-pu-sa__mehr" aria-label="Aktion der Klasse öffnen" title="Aktion der Klasse öffnen"
            onClick={() => oeffneKlassenAktionen(e.ziel.klasse_id ?? null, e.ziel.aktion_id ?? null)}><MIcon name="open_in_new" size={16} /></button>
        </span>
      );
      default: return null;
    }
  }

  const anzahl = daten?.steht_an.length ?? 0;
  const gekuerzt = max != null && anzahl > max;
  const punkt = <button type="button" className="ld-pu-link" onClick={onNeuerPunkt}>+ Punkt</button>;
  return (
    <Karte icon="flag" titel={<>Steht an{anzahl > 0 && max == null && <span className="ld-pu-zaehler">{anzahl}</span>}</>}
      rechts={gekuerzt
        ? <button type="button" className="ld-pu-link" aria-expanded={alle} onClick={() => setAlle(a => !a)}>{alle ? 'Weniger' : `Alle ${anzahl}`}</button>
        : max == null ? punkt : null}>
      <Zustand daten={daten} fehler={fehler} neuLaden={neuLaden}>{() => {
        const liste = gekuerzt && !alle ? daten!.steht_an.slice(0, max) : daten!.steht_an;
        if (liste.length === 0) return <>
          <div className="ld-pu-leer">Gerade steht nichts an.</div>
          {max != null && <div className="ld-ps-punkt">{punkt}</div>}
        </>;
        return (<>
          <ul className="ld-pu-sa">
            {liste.map((e, i) => (
              <li key={`${e.art}-${i}`} className={`ld-pu-sa__eintrag${e.art === 'paragraph68' ? ' ld-pu-sa__eintrag--68' : ''}`}>
                <DatumBox e={e} />
                <div className="ld-pu-sa__text">
                  <b>{e.titel}</b>
                  {e.detail && <span>{e.art === 'nachfassen' ? `„${e.detail}“` : e.detail}</span>}
                  {/* Welle B4: Eltern-Termin in den nächsten 3 Tagen → Blatt fürs Gespräch (unter dem Text, damit die Zeile am Handy Platz hat) */}
                  {e.art === 'elterntermin' && e.blatt && (
                    <button type="button" className="ld-pu-link ld-pu-sa__blatt" onClick={() => oeffneBlatt({ schuelerId: s.id, name: daten?.kopf.name ?? s.name })}>
                      Blatt fürs Gespräch</button>
                  )}
                </div>
                {knopf(e, i)}
              </li>
            ))}
          </ul>
          {max != null && (!gekuerzt || alle) && <div className="ld-ps-punkt">{punkt}</div>}
        </>);
      }}</Zustand>
    </Karte>
  );
}

// ── Verlauf ──────────────────────────────────────────────────────────────────
const FILTER: { key: VerlaufTyp; label: string }[] = [
  { key: 'alle', label: 'Alles' }, { key: 'fehlzeiten', label: 'Fehlzeiten' }, { key: 'mitarbeit', label: 'Mitarbeit' },
  { key: 'noten', label: 'Noten' }, { key: 'notizen', label: 'Notizen' }, { key: 'gespraeche', label: 'Gespräche' }, { key: 'mails', label: 'Mails' },
];
const TYP_ICON: Record<VerlaufEintrag['typ'], string> = {
  fehlzeiten: 'event_busy', mitarbeit: 'back_hand', noten: 'star', notizen: 'sticky_note_2', gespraeche: 'forum', mails: 'mail',
};
const QUELLE_TEXT: Record<string, { text: string; icon: string }> = {
  klassenbuch: { text: 'aus dem Klassenbuch', icon: 'menu_book' }, diktat: { text: 'per Diktat', icon: 'mic' },
  schnelldiktat: { text: 'per Diktat', icon: 'mic' }, ausflug: { text: 'aus dem Ausflug', icon: 'directions_bus' },
  mail: { text: 'aus einer Mail zugeordnet', icon: 'link' }, manuell: { text: 'von dir eingetragen', icon: 'edit' },
};
function VerlaufKarte({ s, onTab }: Props) {
  return <VerlaufListe schuelerId={s.id} onTab={onTab} seite={5}
    rechts={<button type="button" className="ld-pu-link" onClick={() => onTab('verlauf')}>Ganzer Verlauf</button>} />;
}

/** Welle P1c: gemeinsame Verlaufsliste — Überblick (5 neueste) und Tab Verlauf (mit Schuljahr) */
export function VerlaufListe({ schuelerId, onTab, seite, rechts, mitSchuljahr = false }: {
  schuelerId: number; onTab: (tab: ProfilTab, anker?: string) => void; seite: number; rechts?: React.ReactNode; mitSchuljahr?: boolean;
}) {
  const [typ, setTyp] = useState<VerlaufTyp>('alle');
  const [schuljahr, setSchuljahr] = useState('laufend');
  const v = useVerlauf(schuelerId, typ, seite, schuljahr);
  const start = (() => { const d = new Date(); return d.getMonth() + 1 >= 8 ? d.getFullYear() : d.getFullYear() - 1; })();
  const jahre = [0, 1, 2].map(i => `${start - i}/${String(start - i + 1).slice(2)}`);
  function oeffne(e: VerlaufEintrag) {
    if (e.typ === 'mails' && typeof e.ziel.mail_id === 'number') { oeffneMailVonAussen(e.ziel.mail_id); return; }
    if (e.typ === 'notizen') { oeffneNotizen(schuelerId); return; }
    const tab = e.ziel.tab as ProfilTab | undefined;
    if (tab) onTab(tab);
  }
  return (
    <Karte icon="timeline" titel="Verlauf" className="ld-pu-verlauf" rechts={rechts}>
      {mitSchuljahr && (
        <div className="ld-nt-umschalter ld-pu-verlauf__sj" role="group" aria-label="Schuljahr">
          {jahre.map((j, i) => { const wert = i === 0 ? 'laufend' : j; return (
            <button key={j} type="button" aria-pressed={schuljahr === wert} className={schuljahr === wert ? 'an' : ''} onClick={() => setSchuljahr(wert)}>Schuljahr {j}</button>); })}
        </div>
      )}
      <div className="ld-pu-filter" role="group" aria-label="Verlauf filtern">
        {FILTER.map(f => (
          <button key={f.key} type="button" aria-pressed={typ === f.key} className={`ld-pu-filter__chip${typ === f.key ? ' an' : ''}`} onClick={() => setTyp(f.key)}>{f.label}</button>
        ))}
      </div>
      {v.fehler && !v.eintraege ? <Fehler onErneut={v.neuLaden} />
        : !v.eintraege ? <Laedt />
        : v.eintraege.length === 0 ? <div className="ld-pu-leer">{typ === 'alle' ? 'In diesem Schuljahr ist noch nichts passiert.' : 'Dazu gibt es in diesem Schuljahr noch keinen Eintrag.'}
            {mitSchuljahr && schuljahr === 'laufend' && v.aeltere > 0 && <button type="button" className="ld-pu-link" onClick={() => setSchuljahr(jahre[1])}>{v.aeltere} ältere Einträge ansehen</button>}</div>
        : (
          <>
            <ul className="ld-pu-vl">
              {v.eintraege.map(e => {
                const q = e.quelle ? QUELLE_TEXT[e.quelle] : null;
                const klickbar = true;
                const inhalt = (<>
                  <span className="ld-pu-vl__datum">{wtTm(e.datum)}</span>
                  <span className={`ld-pu-vl__icon ld-pu-vl__icon--${e.typ}`}><MIcon name={TYP_ICON[e.typ]} size={18} /></span>
                  <span className="ld-pu-vl__text">
                    <b>{e.titel}</b>
                    {e.detail && <span>{e.typ === 'mails' ? `„${e.detail}“` : e.detail}</span>}
                    {q && e.typ !== 'mails' && <small><MIcon name={q.icon} size={13} />{q.text}</small>}
                  </span>
                </>);
                return (
                  <li key={`${e.typ}-${e.id}`}>
                    {klickbar ? <button type="button" className="ld-pu-vl__zeile" onClick={() => oeffne(e)}>{inhalt}</button>
                      : <div className="ld-pu-vl__zeile">{inhalt}</div>}
                  </li>
                );
              })}
            </ul>
            {v.eintraege.length < v.gesamt && (
              <button type="button" className="ld-pu-mehr" onClick={v.weitere} disabled={v.laedt}>
                {v.laedt ? 'Lädt …' : `Weitere laden (${v.gesamt - v.eintraege.length})`}
              </button>
            )}
            {v.fehler && <Fehler onErneut={v.weitere} />}
            {mitSchuljahr && schuljahr === 'laufend' && v.eintraege.length >= v.gesamt && v.aeltere > 0 && (
              <button type="button" className="ld-pu-mehr" onClick={() => setSchuljahr(jahre[1])}>{v.aeltere} ältere Einträge aus früheren Schuljahren</button>
            )}
          </>
        )}
    </Karte>
  );
}

// ── Eltern ───────────────────────────────────────────────────────────────────
const ROLLE: Record<ElternKontakt['rolle'], string> = { mutter: 'Mutter', vater: 'Vater', grosseltern: 'Großeltern', betreuer: 'Betreuer/in', unbekannt: 'Elternteil' };
const KONTAKT_ART = { mail: 'Mail', gespraech: 'Gespräch', telefonat: 'Telefonat' } as const;
function ElternKarte({ s, daten, fehler, neuLaden, isMobile, onElternNeu, onElternBearbeiten }: Props) {
  const vorname = daten?.kopf.vorname ?? s.name.split(' ')[0];
  return (
    // Welle P1d: am Handy legt die Leiste unten („+ Kontakt“) an — hier dann kein zweiter Knopf
    <Karte icon="family_restroom" titel="Eltern" rechts={isMobile ? undefined : <button type="button" className="ld-pu-link" onClick={onElternNeu}>+ Hinzufügen</button>}>
      <div id="ld-profil-eltern" />
      <Zustand daten={daten} fehler={fehler} neuLaden={neuLaden}>{() => {
        const liste = daten!.eltern;
        if (liste.length === 0) return <div className="ld-pu-leer">Noch kein Elternkontakt hinterlegt.</div>;
        return (
          <ul className="ld-pu-eltern">
            {liste.map(e => {
              const name = e.name || ROLLE[e.rolle];
              return (
                <li key={e.eltern_schueler_id}>
                  <span className="ld-pu-eltern__avatar" aria-hidden="true">{name.split(' ').map(t => t[0]).join('').slice(0, 2).toUpperCase()}</span>
                  <div className="ld-pu-eltern__text">
                    <b>{name}</b>
                    <span className="ld-pu-eltern__rolle">{ROLLE[e.rolle]}{e.hauptkontakt && <span className="ld-pu-hk">Hauptkontakt</span>}</span>
                    {e.email && <span className="ld-pu-eltern__zeile"><MIcon name="mail" size={14} />{e.email}</span>}
                    {e.telefon
                      ? (isMobile
                        ? <a className="ld-pu-eltern__zeile ld-pu-eltern__tel" href={`tel:${e.telefon.replace(/\s+/g, '')}`}><MIcon name="call" size={14} />{e.telefon}</a>
                        : <span className="ld-pu-eltern__zeile"><MIcon name="call" size={14} />{e.telefon}</span>)
                      : <button type="button" className="ld-pu-link ld-pu-eltern__ergaenzen" onClick={() => onElternBearbeiten(e)}><MIcon name="add_call" size={14} />Telefon ergänzen</button>}
                    {e.letzter_kontakt && (
                      <span className="ld-pu-eltern__letzter">Letzter Kontakt: {KONTAKT_ART[e.letzter_kontakt.art]} am {wtTm(e.letzter_kontakt.datum)}</span>
                    )}
                  </div>
                  <div className="ld-pu-eltern__knoepfe">
                    {e.email && <button type="button" className="ld-pk-icon ld-pk-icon--klein" aria-label={`Mail an ${name}`} title="Mail schreiben"
                      onClick={() => oeffneMailVerfassen(e.email!, vorname)}><MIcon name="mail" size={17} /></button>}
                    {isMobile && e.telefon && <a className="ld-pk-icon ld-pk-icon--klein" aria-label={`${name} anrufen`} href={`tel:${e.telefon.replace(/\s+/g, '')}`}><MIcon name="call" size={17} /></a>}
                    <button type="button" className="ld-pk-icon ld-pk-icon--klein" aria-label={`${name} bearbeiten`} title="Bearbeiten" onClick={() => onElternBearbeiten(e)}><MIcon name="edit" size={16} /></button>
                  </div>
                </li>
              );
            })}
          </ul>
        );
      }}</Zustand>
    </Karte>
  );
}
